/**
 * StreakRestoreModal — REACTIVE streak save flow.
 *
 * Pops on Dashboard mount when the backend reports `can_restore: true`,
 * meaning the user's streak has just broken (within 48h) AND they still
 * have at least 1 save credit this month. One tap = streak restored.
 *
 * Why this exists: the old proactive `POST /streak/save` was confusing —
 * users would "use a save" pre-emptively, then later miss several days
 * and still lose the streak, blaming the system. This reactive flow only
 * spends a save when one is actually needed and shows the visceral
 * before/after, which builds trust in the feature.
 */
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Flame, Shield, X } from "lucide-react";
import { API } from "@/App";

export default function StreakRestoreModal() {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [restoring, setRestoring] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/premium/streak/restore-status`);
        if (cancelled) return;
        setStatus(res.data);
        if (res.data?.can_restore) setOpen(true);
      } catch (_) {
        /* not premium / no token / etc. — silently ignore */
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleRestore = async () => {
    setRestoring(true);
    try {
      const res = await axios.post(`${API}/premium/streak/restore`);
      toast.success(res.data.message || "Your streak is back!");
      setOpen(false);
      // Tell the rest of the app to refresh streak counters
      window.dispatchEvent(new Event("duolyst:streak-refresh"));
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't restore your streak.");
    } finally {
      setRestoring(false);
    }
  };

  // Only fire the dismiss API when the user EXPLICITLY taps "Let it go".
  // Closing the modal via the X / tapping outside / Esc must NOT wipe the
  // snapshot — otherwise a user who misclicks (or has a transient network
  // failure on the Restore call) loses the chance to retry. Lost Patricia
  // a 50-day streak in production once already.
  const handleLetItGo = async () => {
    try {
      await axios.post(`${API}/premium/streak/dismiss-break`);
    } catch (_) { /* best-effort */ }
    setOpen(false);
  };

  if (!status?.can_restore) return null;

  const prev = status.previous_streak;
  const hrs = status.hours_remaining;
  const saves = status.saves_remaining;

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent
        className="max-w-sm bg-card border-none rounded-2xl"
        data-testid="streak-restore-modal"
      >
        <DialogHeader>
          <div className="flex items-center justify-center w-16 h-16 mx-auto rounded-full bg-gradient-to-br from-orange-500/20 to-pink-500/20 mb-2">
            <Flame className="w-8 h-8 text-orange-500" />
          </div>
          <DialogTitle className="font-serif text-xl text-center">
            Your {prev}-day streak just broke
          </DialogTitle>
          <DialogDescription className="text-center">
            Restore it with 1 Streak Save and pick up right where you left off.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 pt-2">
          <div className="rounded-xl bg-muted/30 px-4 py-3 text-sm space-y-2">
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Streak before</span>
              <span className="font-semibold">🔥 {prev}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">After restore</span>
              <span className="font-semibold text-orange-500">🔥 {prev}</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Saves available</span>
              <span className="font-semibold">{saves} left this month</span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-muted-foreground">Window closes in</span>
              <span className="font-semibold">{hrs}h</span>
            </div>
          </div>

          <Button
            onClick={handleRestore}
            disabled={restoring}
            className="w-full bg-gradient-to-r from-orange-500 to-pink-500 text-white rounded-xl h-11"
            data-testid="streak-restore-confirm"
          >
            <Shield className="w-4 h-4 mr-2" />
            {restoring ? "Restoring…" : `Restore my ${prev}-day streak (1 save)`}
          </Button>
          <Button
            onClick={handleLetItGo}
            variant="ghost"
            className="w-full rounded-xl h-10 text-muted-foreground"
            data-testid="streak-restore-dismiss"
          >
            <X className="w-4 h-4 mr-2" />
            Let it go
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
